import moment from "moment-timezone";
import * as sections from "components/Homeowner/CaseForms/ComparableGrid/Sections/Sections";
import { get } from "lodash";
import { hasValue } from "components/Homeowner/CaseForms/ComparableGrid/Misc";
import { CompGridFormCodes } from "components/Homeowner/CaseForms/ComparableGrid/CompGridFormCodes";

const getSummaryDisplayValue = (choices, storedValue) => get(choices.find(x => x.storedValue === storedValue), "summaryDisplayValue") || "";

export const getLocationUAD = (
  [locationRating, locationType, locationType1Other, locationType2, locationType2Other],
  locationRatingChoices,
  locationTypeChoices
) => {
  if (!locationRating || !locationType) {
    return null;
  }

  const locationTypeSummary = (locationType, locationTypeOther) => {
    if (!locationType) return null;
    if (locationType !== "Other") return locationType;
    else if (locationType === "Other" && locationTypeOther) return locationTypeOther;
    else return null;
  };

  let locationType1SummaryDisplay = locationTypeSummary(getSummaryDisplayValue(locationTypeChoices, locationType), locationType1Other);
  let locationType2SummaryDisplay = locationTypeSummary(getSummaryDisplayValue(locationTypeChoices, locationType2), locationType2Other);

  return `${getSummaryDisplayValue(locationRatingChoices, locationRating)}${locationType1SummaryDisplay ? ";" + locationType1SummaryDisplay : ""}${locationType2SummaryDisplay ? ";" + locationType2SummaryDisplay : ""}`;
};

export const getDateOfSaleTimeUAD = ([statusType, saleDate, contractDate], statusTypeChoices) => {
  if (!statusType) return null;
  if (statusType === "Active") return "Active";
  if (!saleDate) return null;
  let saleDateObj = moment(saleDate).format("MM/YY");
  if (statusType === "SettledSale") {
    let contractDateObj = moment(contractDate).format("MM/YY");
    return `${get(statusTypeChoices.find(x => x.storedValue === statusType), "summaryDisplayValue") || ""}${saleDateObj};${contractDate
      ? `${get(statusTypeChoices.find(x => x.storedValue === "Contract"), "summaryDisplayValue") || ""}${contractDateObj}`
      : `Unk`
      }`;
  }
  // for remaining status types
  if (statusTypeChoices.map(x => x.storedValue).includes(statusType)) {
    return `${get(statusTypeChoices.find(x => x.storedValue === statusType), "summaryDisplayValue") || ""}${saleDateObj}`;
  }
  return null;
};

export const getSiteUAD = ([squareFeet, acres]) => {
  if (!hasValue(squareFeet) && !hasValue(acres)) return null;
  // if one of them is present then use that value
  if ((hasValue(squareFeet) || hasValue(acres)) && !(hasValue(squareFeet) && hasValue(acres))) return (hasValue(squareFeet) && `${Number(squareFeet).toFixed(0)} sf`) || (hasValue(acres) && `${Number(acres).toFixed(2)} ac`);
  // if acres > 1 use acres
  return acres >= 1 ? `${Number(acres).toFixed(2)} ac` : `${Number(squareFeet).toFixed(0)} sf`;
};

export const getViewUAD = ([viewRating, viewType, viewType2], viewRatingChoices, viewTypeChoices) =>
  !viewRating || !viewType || viewType === "Other"
    ? null
    : `${get(viewRatingChoices.find(x => x.storedValue === viewRating), "summaryDisplayValue") || ""};${get(
      viewTypeChoices.find(x => x.storedValue === viewType), "summaryDisplayValue") || ""
    }${viewType2 ? ";" + get(
      viewTypeChoices.find(x => x.storedValue === viewType2), "summaryDisplayValue") : ""}`;

export const getDesignUAD = ([attDet, stories, design], attachedDetachedChoices) => {
  return !attDet || !stories || !design
    ? null
    : `${get(attachedDetachedChoices.find(x => x.storedValue === attDet), "summaryDisplayValue") || ""
    }${stories};${design}`;
};

export const getBelowGradeUAD = ([totalSquareFeet, finishedSquareFeet, basementExitType], basementExitTypeChoices, compGridFormCode) => {
  if (compGridFormCode && [CompGridFormCodes.BBT_COMPARABLES_GRID_MUELLER_ENTERPRISE, CompGridFormCodes.COLLATERAL_APPRAISAL_COMPARABLES, CompGridFormCodes.CHASE_SERVICING_COMPARABLES].includes(compGridFormCode)) {
    if (totalSquareFeet === 0 || totalSquareFeet === "0") return "None";
  }
  return (
    (
      hasValue(totalSquareFeet) &&
      hasValue(finishedSquareFeet) &&
      basementExitType
    ) ?
      `${totalSquareFeet}sf${finishedSquareFeet}sf${get(basementExitTypeChoices.find(x => x.storedValue === basementExitType), "summaryDisplayValue") || ""
      }`
      : ""
  );
};

export const getHeatingCoolingUAD = ([heating, cooling], heatingTypeChoices, coolingTypeChoices) =>
  heating &&
  cooling &&
  `${get(heatingTypeChoices.find(x => x.storedValue === heating), "summaryDisplayValue") || ""}/${get(coolingTypeChoices.find(x => x.storedValue === cooling), "summaryDisplayValue") || ""
  }`;

export const getGarageCarportUAD = (
  [driveway, numCars, carStorage1, attType1, numCars1, carStorage2, attType2, numCars2],
  carStorageChoices,
  carStorageAttachmentTypeChoices
) => {
  const garageUAD = (carStorage, attType, numCars) => `${((carStorage === "Garage") || carStorage === "Carport") ? numCars || (numCars === 0 ? "0" : "") : ""}${hasValue(numCars)
    ? (get(carStorageChoices.find(x => x.storedValue === carStorage), "summaryDisplayValue") || "") + (get(carStorageAttachmentTypeChoices.find(x => x.storedValue === attType), "summaryDisplayValue") || "") : ""}`;

  return ((driveway?.length == 0 || driveway?.toUpperCase() === "NONE" || driveway?.toUpperCase() === "NO") && carStorage1 === "None") 
    ? "None" 
    : `${garageUAD(carStorage1, attType1, numCars1)}${garageUAD(carStorage2, attType2, numCars2)}${driveway?.length > 0 && hasValue(numCars) ? numCars + "dw" : ""}`;
};

export const getFinancialConcessionsUAD = ([financingType, concessionAmount], financingTypeChoices) => (
  financingType && (concessionAmount || concessionAmount === 0) ? `${get(financingTypeChoices.find(x => x.storedValue === financingType), "summaryDisplayValue")};${concessionAmount}` : ""
);

export const calculateUAD = (table, settings) => {
  let dir = "left";
  let setValue = (comp, key, dir, callback) => {
    if (comp && comp[key] && comp[key][dir]) {
      comp[key][dir].value = callback();
    }
  };

  table.map(comp => {
    comp[sections.FEATURE].right.value === "Subject" ? dir = "right" : dir = "left";
    let callbackFuncs = {
      [sections.DATE_OF_SALE_TIME]: () => getDateOfSaleTimeUAD(
        [sections.STATUS_TYPE, sections.SALE_DATE_1004P, sections.CONTRACT_DATE].map(x => comp[x][dir].value),
        settings.statusTypeChoices
      ),
      [sections.SITE]: () => getSiteUAD([sections.SQUARE_FEET, sections.ACRES].map(x => comp[x][dir].value), settings.statusTypeChoices),
      [sections.SITE_BBT]: () => getSiteUAD([sections.SQUARE_FEET, sections.ACRES].map(x => comp[x][dir].value), settings.statusTypeChoices),
      [sections.DESIGN_STYLE]: () => getDesignUAD(
        [sections.ATT_DET, sections.STORIES, sections.DESIGN].map(x => comp[x][dir].value),
        settings.attachedDetachedChoices
      ),
      [sections.BASEMENT_FINISHED]: () => getBelowGradeUAD(
        [sections.BELOW_GRADE_TOT_SF, sections.BELOW_GRADE_FIN_SF, sections.BASEMENT_EXIT_TYPE].map(x => comp[x][dir].value),
        settings.basementExitTypeChoices,
        settings.compGridFormCode
      ),
      [sections.BASEMENT_FINISHED_BBT]: () => getBelowGradeUAD(
        [sections.BELOW_GRADE_TOT_SF_BBT, sections.BELOW_GRADE_FIN_SF_BBT, sections.BASEMENT_EXIT_TYPE_BBT].map(x => comp[x][dir].value),
        settings.basementExitTypeChoices,
        settings.compGridFormCode
      ),
      [sections.GARAGE_CARPORT]: () => getGarageCarportUAD(
        [sections.DRIVEWAY, sections.NUM_OF_CARS, sections.CAR_STORAGE_1, sections.ATTACHMENT_TYPE_1, sections.NUM_OF_CARS_1, sections.CAR_STORAGE_2, sections.ATTACHMENT_TYPE_2, sections.NUM_OF_CARS_2].map(x => comp[x][dir].value),
        settings.carStorageChoices,
        settings.carStorageAttachmentTypeChoices
      ),
      [sections.GARAGE_CARPORT_BBT]: () => getGarageCarportUAD(
        [sections.DRIVEWAY_BBT, sections.NUM_OF_CARS_BBT, sections.CAR_STORAGE_1_BBT, sections.ATTACHMENT_TYPE_1_BBT, sections.NUM_OF_CARS_1_BBT, sections.CAR_STORAGE_2_BBT, sections.ATTACHMENT_TYPE_2_BBT, sections.NUM_OF_CARS_2_BBT].map(x => comp[x][dir].value),
        settings.carStorageChoices,
        settings.carStorageAttachmentTypeChoices
      ),
      [sections.GARAGE_CARPORT_MVALUE]: () => getGarageCarportUAD(
        [sections.DRIVEWAY_ENABLED, sections.NUM_OF_CARS_MVALUE, sections.CAR_STORAGE_1_MVALUE, sections.ATTACHMENT_TYPE_1_MVALUE, sections.NUM_OF_CARS_1_MVALUE, sections.CAR_STORAGE_2_MVALUE, sections.ATTACHMENT_TYPE_2_MVALUE, sections.NUM_OF_CARS_2_MVALUE].map(x => comp[x][dir].value),
        settings.carStorageChoices,
        settings.carStorageAttachmentTypeChoices
      ),
      [sections.LOCATION]: () => getLocationUAD(
        [sections.LOCATION_RATING, sections.LOCATION_TYPE, sections.LOCATION_TYPE_1_OTHER, sections.LOCATION_TYPE_2, sections.LOCATION_TYPE_2_OTHER].map(x => comp[x] ? comp[x][dir].value : null),
        settings.locationRatingChoices,
        settings.locationTypeChoices
      ),
      [sections.HEATING_COOLING]: () => getHeatingCoolingUAD(
        [sections.HEATING_1004P, sections.COOLING_1004P].map(x => comp[x][dir].value),
        settings.heatingTypeChoices,
        settings.coolingTypeChoices
      ),
      [sections.VIEW]: () => getViewUAD([sections.VIEW_RATING, sections.VIEW_TYPE, sections.VIEW_TYPE_2].map(x => comp[x][dir].value), settings.viewRatingChoices, settings.viewTypeChoices),
      [sections.SALE_OR]: () => get(settings.saleTypeChoices.find(x => x.storedValue === comp[sections.SALE_1004P][dir].value), "summaryDisplayValue"),
      [sections.FINANCING_CONCESSIONS]: () => getFinancialConcessionsUAD([sections.FINANCING_TYPE, sections.CONCESSION_AMOUNT].map(x => comp[x][dir].value), settings.financingTypeChoices),
      [sections.PORCH_PATIO_DECK]: () => [sections.PORCH, sections.PATIO_DECK].map(x => comp[x][dir].value).filter(Boolean).join(";"),
      [sections.PRIMARY_PHOTO]: () => comp[sections.PRIMARY_PHOTO][dir].value
    };

    for (const section in callbackFuncs) {
      setValue(comp, section, dir, callbackFuncs[section]);
    }

    return comp;
  });
  return table;
};